/**
 * @genesis_domain Settings — Preferences Deep Merge Utility
 * @genesis_version V9.1
 * @genesis_intent Pure, strictly typed deep merge utility for UserPreferences JSONB
 *   partial updates. When a PATCH payload arrives with only a subset of preference
 *   categories (e.g., { export: { defaultFormat: 'pdf' } }), this function merges
 *   the nested sub-objects without destroying sibling keys. Preserves legitimate
 *   false and 0 values via Nullish Coalescing (??) — Logical OR (||) is banned.
 * @genesis_boundaries FORBIDDEN from using any, as any, or @ts-ignore.
 *   FORBIDDEN from using Logical OR (||) for fallbacks — Nullish Coalescing (??) only.
 *   FORBIDDEN from mutating the input arguments (pure function).
 *   FORBIDDEN from performing side effects: no database calls, no API requests, no file I/O.
 * @genesis_ai_trail Phase 75E State Integrity Strike — resolves the destructive partial
 *   update bug where PATCH /api/preferences was overwriting the entire JSONB column,
 *   deleting the user's theme selection. This utility is consumed by both the backend
 *   command service (userProfileCommandService.ts) and the frontend optimistic cache
 *   handler (usePreferencesMutation.ts) to ensure identical merge semantics.
 */

import type { UserPreferences } from '../schemas/userProfile.js';

/** Schema-aligned default for the export sub-object (mirrors Zod .default() values). */
const DEFAULT_EXPORT: NonNullable<UserPreferences['export']> = {
    defaultFormat: 'json-ld',
    includeImages: true,
    includeNutrition: true,
    includeDescription: true,
    includeMeta: true,
    includeIngredients: true,
    includeInstructions: true,
    includeSource: true,
} as const;

/** Schema-aligned default for the measurements sub-object (mirrors Zod .default() values). */
const DEFAULT_MEASUREMENTS: NonNullable<UserPreferences['measurements']> = {
    units: 'us',
} as const;

/**
 * Deeply merges an incoming partial preferences payload into the existing
 * preferences object. For each top-level category key (export, appearance,
 * measurements), the incoming sub-object is spread over the existing sub-object,
 * preserving sibling keys that were not included in the PATCH payload.
 *
 * @param existing - The full existing preferences object (never null/undefined at runtime).
 * @param incoming - The partial PATCH payload — may omit entire categories or individual fields.
 * @returns A new merged preferences object (does not mutate inputs).
 */
export function deepMergePreferences(
    existing: UserPreferences,
    incoming: Partial<UserPreferences>,
): UserPreferences {
    const merged: UserPreferences = {};

    // --- export category ---
    if (incoming.export !== undefined || existing.export !== undefined) {
        const base = existing.export ?? DEFAULT_EXPORT;
        merged.export = {
            ...base,
            ...(incoming.export ?? {}),
        } as typeof base;
    }

    // --- appearance category (all optional fields, no required defaults needed) ---
    if (incoming.appearance !== undefined || existing.appearance !== undefined) {
        merged.appearance = {
            ...(existing.appearance ?? {}),
            ...(incoming.appearance ?? {}),
        };
    }

    // --- measurements category ---
    if (incoming.measurements !== undefined || existing.measurements !== undefined) {
        const base = existing.measurements ?? DEFAULT_MEASUREMENTS;
        merged.measurements = {
            ...base,
            ...(incoming.measurements ?? {}),
        } as typeof base;
    }

    return merged;
}